import { Box } from '@mui/material';

/**
 * Spacing variants for consistent vertical rhythm
 * 
 * - tight: 8px - Between tightly related elements (price → address)
 * - normal: 16px - Standard spacing between elements
 * - section: 24px - Between distinct sections
 * - large: 32px - Major section breaks
 */
export const VERTICAL_SPACING_VARIANTS = {
  TIGHT: 'tight',
  NORMAL: 'normal',
  SECTION: 'section',
  LARGE: 'large',
  EXTRA_TIGHT: 'extra-tight',
} as const;

export type VerticalSpacingVariant = typeof VERTICAL_SPACING_VARIANTS[keyof typeof VERTICAL_SPACING_VARIANTS];

const VARIANT_VALUES: Record<VerticalSpacingVariant, number> = {
  [VERTICAL_SPACING_VARIANTS.TIGHT]: 8,
  [VERTICAL_SPACING_VARIANTS.NORMAL]: 16,
  [VERTICAL_SPACING_VARIANTS.SECTION]: 24,
  [VERTICAL_SPACING_VARIANTS.LARGE]: 32,
  [VERTICAL_SPACING_VARIANTS.EXTRA_TIGHT]: 5,
};

export interface VerticalSpacingProps {
  /** Spacing variant */
  variant: VerticalSpacingVariant;
}

/**
 * VerticalSpacing - Explicit vertical spacing component
 * 
 * Use this instead of CSS margins for clearer, more maintainable layouts.
 * Variant is required - no default assumed.
 * 
 * @example
 * ```tsx
 * <Price />
 * <VerticalSpacing variant="tight" />
 * <Address />
 * <VerticalSpacing variant="section" />
 * <Stats />
 * ```
 */
export function VerticalSpacing({ variant }: VerticalSpacingProps) {
  const height = VARIANT_VALUES[variant];

  return (
    <Box
      aria-hidden="true"
      sx={{ height, flexShrink: 0 }}
    />
  );
}

export default VerticalSpacing;
